import { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import KpiCard from "@/components/KpiCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Building2, Users, DollarSign, Ban, Plus, Trash2, Lock, Unlock, Loader2, Monitor, Copy,
} from "lucide-react";

export default function SuperAdmin() {
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", admin_name: "", admin_email: "", admin_password: "", primary_color: "#003366", plan: "Pro" });

  const load = useCallback(async () => {
    const [s, c] = await Promise.all([api.get("/admin/stats"), api.get("/admin/companies")]);
    setStats(s.data); setCompanies(c.data); setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name || !form.admin_email || !form.admin_password || !form.admin_name) return toast.error("Completa los campos");
    setSaving(true);
    try {
      await api.post("/admin/companies", form);
      toast.success("Empresa creada");
      setOpen(false);
      setForm({ name: "", admin_name: "", admin_email: "", admin_password: "", primary_color: "#003366", plan: "Pro" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggleBlock = async (c) => {
    const status = c.status === "blocked" ? "active" : "blocked";
    await api.patch(`/admin/companies/${c.id}`, { status });
    toast.success(status === "blocked" ? "Empresa bloqueada" : "Empresa desbloqueada");
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar empresa y todos sus datos?")) return;
    await api.delete(`/admin/companies/${id}`);
    toast.success("Empresa eliminada"); load();
  };

  const copyKiosk = (slug) => {
    navigator.clipboard.writeText(`${window.location.origin}/kiosk/${slug}`);
    toast.success("Enlace de quiosco copiado");
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Empresas Cliente</h1>
          <p className="text-slate-500 text-sm mt-1">Gestiona los tenants de la plataforma.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="brand-btn" data-testid="add-company-btn"><Plus className="h-4 w-4 mr-2" /> Alta de empresa</Button></DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle className="font-heading">Nueva empresa cliente</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Nombre de empresa *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="company-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Plan</Label><Input value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} className="mt-1" data-testid="company-plan" /></div>
                <div><Label>Color</Label><div className="flex gap-2 mt-1"><input type="color" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="h-10 w-12 rounded border cursor-pointer" data-testid="company-color" /><Input value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="font-mono" /></div></div>
              </div>
              <div className="pt-2 border-t border-slate-100"><div className="text-xs font-bold tracking-wide uppercase text-slate-400 mb-2">Administrador</div></div>
              <div><Label>Nombre admin *</Label><Input value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} className="mt-1" data-testid="admin-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Email *</Label><Input type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} className="mt-1" data-testid="admin-email" /></div>
                <div><Label>Contraseña *</Label><Input value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} className="mt-1" data-testid="admin-password" /></div>
              </div>
            </div>
            <DialogFooter><Button onClick={create} disabled={saving} className="brand-btn" data-testid="save-company-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crear empresa"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {!stats ? <div className="h-32 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div> : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard testId="sa-companies" label="Empresas activas" value={stats.active_companies} icon={Building2} accent />
          <KpiCard testId="sa-employees" label="Empleados totales" value={stats.total_employees} icon={Users} />
          <KpiCard testId="sa-mrr" label="MRR" value={`${stats.mrr}€`} icon={DollarSign} />
          <KpiCard testId="sa-blocked" label="Bloqueadas" value={stats.blocked_companies} icon={Ban} />
        </div>
      )}

      <Card className="border-slate-200 shadow-none overflow-hidden">
        {loading ? <div className="h-48 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div> : (
          <Table>
            <TableHeader><TableRow className="hover:bg-transparent">
              <TableHead>Empresa</TableHead><TableHead>Plan</TableHead><TableHead>Empleados</TableHead>
              <TableHead>Precio/mes</TableHead><TableHead>Estado</TableHead><TableHead className="text-right">Acciones</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {companies.map((c) => (
                <TableRow key={c.id} className="group" data-testid={`company-row-${c.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-lg flex items-center justify-center text-white text-xs font-bold" style={{ background: c.primary_color }}>{c.name[0]}</div>
                      <div><div className="font-medium text-slate-800">{c.name}</div><div className="text-xs text-slate-400">/{c.slug}</div></div>
                    </div>
                  </TableCell>
                  <TableCell><Badge variant="secondary">{c.plan}</Badge></TableCell>
                  <TableCell className="text-slate-600">{c.employee_count}</TableCell>
                  <TableCell className="text-slate-600">{c.monthly_price}€</TableCell>
                  <TableCell><Badge className={`border-0 ${c.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>{c.status === "active" ? "Activa" : "Bloqueada"}</Badge></TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                      <Button size="icon" variant="ghost" onClick={() => copyKiosk(c.slug)} title="Copiar quiosco" data-testid={`kiosk-${c.id}`}><Monitor className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => toggleBlock(c)} data-testid={`block-${c.id}`} className={c.status === "active" ? "hover:text-amber-600" : "hover:text-emerald-600"}>{c.status === "active" ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}</Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(c.id)} className="hover:text-red-500" data-testid={`del-company-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
